import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const CartContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CartProvider = ({ children }) => {
  const [cartId, setCartId] = useState(null);
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [cartCount, setCartCount] = useState(0);

  const fetchCart = useCallback(async (id) => {
    try {
      const response = await axios.get(`${API}/cart/${id}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }, []);

  useEffect(() => {
    const storedCartId = localStorage.getItem('cartId');
    if (storedCartId) {
      setCartId(storedCartId);
      fetchCart(storedCartId);
    }
  }, [fetchCart]);

  useEffect(() => {
    const count = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    setCartCount(count);
  }, [cart.items]);

  const addToCart = useCallback(async (productId, quantity = 1, variant = null) => {
    try {
      const payload = { product_id: productId, quantity };
      if (variant) payload.variant = variant;

      if (cartId) {
        const response = await axios.post(`${API}/cart/${cartId}/add`, payload);
        setCart(response.data);
      } else {
        const response = await axios.post(`${API}/cart`, payload);
        setCartId(response.data.id);
        localStorage.setItem('cartId', response.data.id);
        setCart(response.data);
      }
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      return false;
    }
  }, [cartId]);

  const removeFromCart = useCallback(async (productId) => {
    if (!cartId) return;
    try {
      await axios.delete(`${API}/cart/${cartId}/item/${productId}`);
      await fetchCart(cartId);
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  }, [cartId, fetchCart]);

  const clearCart = useCallback(() => {
    setCartId(null);
    setCart({ items: [], total: 0 });
    localStorage.removeItem('cartId');
  }, []);

  const value = useMemo(() => ({
    cart, cartCount, cartId, addToCart, removeFromCart, clearCart, fetchCart
  }), [cart, cartCount, cartId, addToCart, removeFromCart, clearCart, fetchCart]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
